import React, { useMemo } from "react";
import { connect } from "react-redux";

import cn from "classnames";
import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared/enum";
import ImportMapping from "@mapmycustomers/shared/types/imports/ImportMapping";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";

import InterceptNavigationModal from "@app/component/InterceptNavigationModal";
import Table from "@app/component/Table";
import TimeZoneSelect from "@app/component/TimeZoneSelect";
import TIMEZONE_ENTITIES from "@app/scene/settings/component/Imports/enum/TIMEZONE_ENTITIES";
import { setMappingField, setTimezone } from "@app/scene/settings/component/Imports/store/actions";
import {
  getEntityType,
  getFilePreview,
  getMappedColumns,
  getMappingOptions,
  getTimezone,
  isConfigModified,
} from "@app/scene/settings/component/Imports/store/selectors";
import ImportMappingOption from "@app/scene/settings/component/Imports/type/ImportMappingOption";
import ImportMappingTableRow from "@app/scene/settings/component/Imports/type/ImportMappingTableRow";
import { MAPPING_SKIP_KEY } from "@app/scene/settings/component/Imports/util/consts";
import { RootState } from "@app/store/rootReducer";
import FilePreview from "@app/types/FilePreview";
import TimeZone from "@app/types/TimeZone";

import Config from "./Config";
import styles from "./FieldMappingStep.module.scss";
import MmcFieldInfoModal from "./MmcFieldInfoModal";
import useMappingTableConfig from "./useMappingTableConfig";

interface Props {
  className?: string;
  entityType: EntityType;
  filePreview: FilePreview;
  isConfigModified: boolean;
  mappedColumns: ImportMapping;
  mappingOptions: ImportMappingOption[];
  setMappingField: typeof setMappingField;
  setTimezone: typeof setTimezone;
  timezone: TimeZone;
}

const FieldMappingStep: React.FC<Props> = ({
  className,
  entityType,
  filePreview,
  isConfigModified,
  mappedColumns,
  mappingOptions,
  setMappingField,
  setTimezone,
  timezone,
}) => {
  const intl = useIntl();

  const [mmcFieldInfoModalVisible, showMmcFieldInfoModal, hideMmcFieldInfoModal] = useBoolean();

  const mappingTableConfig = useMappingTableConfig(
    intl,
    mappingOptions,
    mappedColumns,
    setMappingField,
    showMmcFieldInfoModal
  );

  const tableData: ImportMappingTableRow[] = useMemo(
    () =>
      filePreview.headers.map((header: string, index) => {
        const mmcField = Object.keys(mappedColumns).find((key) => mappedColumns[key] === header);
        const isUnique = mappingOptions.find(({ label }) => mmcField === label)?.unique;
        return {
          fileField: header,
          isMmcFieldUnique: isUnique ?? false,
          matched: false,
          mmcField: mmcField || MAPPING_SKIP_KEY,
          preview: filePreview.preview.length > 0 ? filePreview.preview[0][index] : "",
        };
      }),
    [filePreview.headers, filePreview.preview, mappedColumns, mappingOptions]
  );

  return (
    <div className={cn(styles.mappingContainer, className)}>
      {TIMEZONE_ENTITIES.has(entityType) && (
        <div className={styles.timezoneFieldContainer}>
          <span>
            {intl.formatMessage({
              id: "settings.newImports.matchFieldsStep.timezoneDescription",
              defaultMessage: "Select a timezone to apply to Activities fields",
              description: "Import field matching step time zone description",
            })}
          </span>
          <TimeZoneSelect
            className={styles.timezoneSelectField}
            onChange={setTimezone}
            size="middle"
            value={timezone}
          />
        </div>
      )}
      <div className={styles.container}>
        <Table<ImportMappingTableRow>
          className={styles.table}
          columns={mappingTableConfig}
          dataSource={tableData}
          noHover
          pagination={false}
          rowKey="fileField"
          scrollable
          size="small"
        />
        <Config className={styles.footer} />
      </div>
      <MmcFieldInfoModal onHide={hideMmcFieldInfoModal} visible={mmcFieldInfoModalVisible} />
      <InterceptNavigationModal
        leaveButtonText={intl.formatMessage({
          id: "settings.newImports.matchFieldsStep.navigationPrompt.leaveButton",
          defaultMessage: "Leave without saving",
          description: "Field mapping step block leave button title",
        })}
        shouldShowPrompt={isConfigModified}
        stayButtonText={intl.formatMessage({
          id: "settings.newImports.matchFieldsStep.navigationPrompt.stayButton",
          defaultMessage: "Finish field matching",
          description: "Field mapping step block stay button title",
        })}
        subTitle={intl.formatMessage({
          id: "settings.newImports.matchFieldsStep.navigationPrompt.subTitle",
          defaultMessage: "You will lose your progress in matching fields if you exit",
          description: "Field mapping step block sub title",
        })}
        title={intl.formatMessage({
          id: "settings.newImports.matchFieldsStep.navigationPrompt.title",
          defaultMessage: "Are you sure you want to leave this page?",
          description: "Field mapping step block title",
        })}
      />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  entityType: getEntityType(state),
  filePreview: getFilePreview(state)!,
  isConfigModified: isConfigModified(state),
  mappedColumns: getMappedColumns(state),
  mappingOptions: getMappingOptions(state),
  timezone: getTimezone(state),
});

const mapDispatchToProps = {
  setMappingField,
  setTimezone,
};

export default connect(mapStateToProps, mapDispatchToProps)(FieldMappingStep);
